/**
 * The viewer's network half — enroll (Task 4), resolve (Task 7), and
 * read (Task 8). Every path below is now a RELATIVE, same-origin path
 * (Mathew's ruling, recorded in issue #95: "Shape A, ONE ORIGIN — the
 * Worker mounts on zenofvenn.com zone routes, the viewer is same-origin,
 * CORS disappears entirely"). There used to be an `apiOrigin`/
 * `ZOV_API_ORIGIN` seam here for a cross-origin topology that was never
 * actually decided; now that it's decided, carrying the seam forward
 * would be pretending an open question still existed. What issue #95
 * still owns is the DEPLOYMENT wiring (the actual zone-route config);
 * this file's only job was to stop guessing at a different topology,
 * which it now does by construction — there is no origin parameter left
 * to guess wrong.
 *
 * Every request/response shape below is checked against the MERGED
 * `workers/` handlers (`enroll.ts`, `resolve.ts`, `read.ts`,
 * `denial.ts`), not assumed — Ferruccio's gate on PR #83, B1/B2, found
 * real drift between an earlier, invented contract and the real one.
 */

import type { VennDocumentV1 } from '@zov/core';
import type { ResolveSignal } from './failure.ts';

export interface EnvelopeResponse {
  doc: VennDocumentV1;
}

export interface EnrolledDevice {
  principal: string;
  secret: string;
}

export type EnrollOutcome =
  | { readonly ok: true; readonly device: EnrolledDevice }
  | { readonly ok: false; readonly signal: ResolveSignal };

export type ResolveOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly signal: ResolveSignal };

export type ReadOutcome =
  | { readonly ok: true; readonly envelope: EnvelopeResponse }
  | { readonly ok: false; readonly signal: ResolveSignal };

/** How long a single enroll/resolve/read attempt is given before this
 *  module gives up on it and treats it as a hiccup — Bianca's gate on PR
 *  #83, blocking: without this, a hung origin left a visitor staring at
 *  Act 1's bloom forever, told nothing. An artisan's call, same sizing
 *  discipline as Task 10's rate limit and this module's own retry
 *  backoff (retry.ts). */
export const REQUEST_TIMEOUT_MS = 8_000;

/** Wraps a `fetch` call so "the network itself failed", "the request
 *  timed out", and "the server answered" collapse into one
 *  `ResolveSignal` shape the caller can hand to `classifyFailure`.
 *  `externalSignal` lets `arrival.ts` cancel an in-flight attempt from
 *  OUTSIDE — a superseded generation, or the tab going into `pagehide` —
 *  by aborting the SAME underlying request this module's own timeout
 *  would abort, rather than inventing a second mechanism. */
async function attempt(
  run: (signal: AbortSignal) => Promise<Response>,
  options?: { timeoutMs?: number; externalSignal?: AbortSignal },
): Promise<{ response: Response } | { signal: ResolveSignal }> {
  const timeoutMs = options?.timeoutMs ?? REQUEST_TIMEOUT_MS;
  const controller = new AbortController();
  const external = options?.externalSignal;
  const forwardAbort = (): void => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', forwardAbort);
  }
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await run(controller.signal);
    return { response };
  } catch {
    return { signal: { kind: 'network-error' } };
  } finally {
    clearTimeout(timer);
    external?.removeEventListener('abort', forwardAbort);
  }
}

async function parseBodyBestEffort(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function denialSignal(
  response: Response,
  body: unknown,
): { ok: false; signal: ResolveSignal } {
  return {
    ok: false,
    signal: { kind: 'http-response', status: response.status, body },
  };
}

/**
 * `POST /devices` (Task 4) — no body, no auth: this IS how a caller gets
 * credentials at all. Ferruccio's gate on PR #83, B2: the viewer never
 * called this before, so its every first-ever `resolve` presented no
 * `Authorization` header and was denied by the router before
 * `resolve.ts`'s own code ran. `device.ts` owns WHERE the returned
 * secret is stored; this function only owns the wire call.
 */
export async function enrollDevice(
  fetchImpl: typeof fetch = fetch,
  externalSignal?: AbortSignal,
): Promise<EnrollOutcome> {
  const result = await attempt(
    (signal) => fetchImpl('/devices', { method: 'POST', signal }),
    { externalSignal },
  );
  if ('signal' in result) return { ok: false, signal: result.signal };
  const body = await parseBodyBestEffort(result.response);
  if (!result.response.ok) return denialSignal(result.response, body);
  const parsed = body as {
    ok?: unknown;
    principal?: unknown;
    secret?: unknown;
  } | null;
  if (
    !parsed ||
    parsed.ok !== true ||
    typeof parsed.principal !== 'string' ||
    typeof parsed.secret !== 'string'
  ) {
    // A 2xx that doesn't match the wire-frozen `{ok,principal,secret}`
    // shape is the server's own hiccup, not a verdict on anything — same
    // bucket as an unparseable body elsewhere in this file.
    return denialSignal(result.response, body);
  }
  return {
    ok: true,
    device: { principal: parsed.principal, secret: parsed.secret },
  };
}

/**
 * `POST /venns/:vennId/resolve` (Task 7) — `capability: 'device-auth'`
 * in the merged `workers/src/routes.ts`: REQUIRES `Authorization:
 * Bearer <device secret>` (Ferruccio's gate, PR #83, B2) in addition to
 * the bearer LINK secret captured by `hashSecret.ts`, which travels in
 * the JSON body as `{ secret }` — two different secrets, two different
 * places, per `resolve.ts`'s own module comment.
 */
export async function resolveLink(
  vennId: string,
  linkSecret: string,
  deviceSecret: string,
  fetchImpl: typeof fetch = fetch,
  externalSignal?: AbortSignal,
): Promise<ResolveOutcome> {
  const result = await attempt(
    (signal) =>
      fetchImpl(`/venns/${encodeURIComponent(vennId)}/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${deviceSecret}`,
        },
        body: JSON.stringify({ secret: linkSecret }),
        signal,
      }),
    { externalSignal },
  );
  if ('signal' in result) return { ok: false, signal: result.signal };
  if (result.response.ok) return { ok: true };
  const body = await parseBodyBestEffort(result.response);
  return denialSignal(result.response, body);
}

/**
 * `GET /venns/:vennId/envelope` (Task 8) — `capability: 'session-auth'`:
 * authority travels ONLY in the `Secure; HttpOnly` session cookie
 * `resolve.ts` mints; `router.ts`'s own session-auth branch never looks
 * at an `Authorization` header at all, so this call sends none. Same-
 * origin `fetch` already sends cookies by default; `credentials:
 * 'include'` is kept for clarity, not because it changes behavior here.
 */
export async function readEnvelope(
  vennId: string,
  fetchImpl: typeof fetch = fetch,
  externalSignal?: AbortSignal,
): Promise<ReadOutcome> {
  const result = await attempt(
    (signal) =>
      fetchImpl(`/venns/${encodeURIComponent(vennId)}/envelope`, {
        method: 'GET',
        credentials: 'include',
        signal,
      }),
    { externalSignal },
  );
  if ('signal' in result) return { ok: false, signal: result.signal };
  if (!result.response.ok) {
    const body = await parseBodyBestEffort(result.response);
    return denialSignal(result.response, body);
  }
  try {
    const envelope = (await result.response.json()) as EnvelopeResponse;
    return { ok: true, envelope };
  } catch {
    return {
      ok: false,
      signal: { kind: 'http-response', status: 502, body: null },
    };
  }
}
